import { KEYBOARD_KEYS } from "@netapp/bxp-design-system-react";
import _ from "lodash";
import { useCallback, useState } from "react";

export const useListNavigation = (items, refs, config = {}) => {
    const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);
    const { 
        onItemClick = _.noop, 
        circular = true, 
        itemSelector = null,
        focusElement = 'self' // 'self' or 'button' or custom function
    } = config;
    
    const handleKeyDown = useCallback((e, index) => {
        const { key } = e;
        let nextIndex = index;

        switch (key) {
            case KEYBOARD_KEYS.ARROW_DOWN:
                e.preventDefault();
                nextIndex = circular && index === items.length - 1 ? 0 : _.min([index + 1, items.length - 1]);
                break;
            case KEYBOARD_KEYS.ARROW_UP:
                e.preventDefault();
                nextIndex = circular && index === 0 ? items.length - 1 : _.max([index - 1, 0]);
                break;
            case KEYBOARD_KEYS.ENTER:
            case KEYBOARD_KEYS.SPACE_BAR:
            case KEYBOARD_KEYS.SPACE_BAR_ALTERNATIVE:
                e.preventDefault();
                if (onItemClick && refs.current[index]) {
                    refs.current[index].click();
                }
                return;
            default:
                return;
        }

        if (nextIndex !== index) {
            setCurrentFocusIndex(nextIndex);
            const targetElement = refs.current[nextIndex];
            
            if (targetElement) {
                if (typeof focusElement === 'function') {
                    focusElement(targetElement);
                } else if (focusElement === 'button') {
                    let button = null;
                    if (targetElement.tagName === 'BUTTON') {
                        button = targetElement;
                    } else if (targetElement.querySelector) {
                        button = itemSelector ? 
                            targetElement.querySelector(itemSelector) : 
                            targetElement.querySelector('button');
                    }
                    button?.focus();
                } else {
                    targetElement.focus();
                }
            }
        }
    }, [items?.length, refs, onItemClick, circular, itemSelector, focusElement]);
    
    const createKeyDownHandler = useCallback((index) => {
        return (e) => handleKeyDown(e, index);
    }, [handleKeyDown]);
    
    const handleFocus = useCallback((index) => {
        setCurrentFocusIndex(index);
    }, []);
    
    return {
        currentFocusIndex,
        createKeyDownHandler,
        handleFocus,
        handleKeyDown
    };
};
